import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { PaymentInfoModal } from "./TopBar";
import { IdentityFields } from "./IdentityFields";
import { useQuickOffer } from "../hooks/useQuickOffer";
import { handleImgError } from "../lib/imageFallback";
import { ChevronLeft, ChevronRight, ShieldCheck, Loader2, Gavel, Tag, Sparkles, Package, BadgeCheck } from "lucide-react";

/**
 * Rich "Detalles" pop-up: photos, description, ficha técnica, optional last
 * price / bid, and the identity/registration form - all in ONE pop-up so a
 * new customer never needs a second one to register before sending a request.
 */
const ProductDetailsPopup = ({ isOpen, onClose, product, participation, onSuccess }) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [lastPriceAmount, setLastPriceAmount] = useState("");
  const [bidAmount, setBidAmount] = useState("");

  const isAuction = product?.sale_type === "auction";
  const listedPrice = product?.promo_price || product?.price;
  const currentBid = product?.current_bid || product?.price || 0;
  const photos = [product?.image_url, ...(product?.images || [])].filter(Boolean);

  const {
    loading, checkingWhatsapp, sessionWhatsapp, whatsapp, setWhatsapp, username, setUsername,
    confirmWhatsappChecked, setConfirmWhatsappChecked, userExists, maskedUsername, whatsappError,
    submitOffer
  } = useQuickOffer({ product, isOpen, onSuccess, onClose });

  useEffect(() => {
    if (isOpen) {
      setPhotoIndex(0);
      setLastPriceAmount("");
      setBidAmount("");
    }
  }, [isOpen, product]);

  if (!product) return null;

  const fichaTecnica = [
    { label: "Condición", value: product.condition_grade, icon: BadgeCheck },
    { label: "Almacenamiento / RAM", value: product.storage_ram, icon: Package },
    { label: "Accesorios incluidos", value: product.included_accessories, icon: Sparkles },
    { label: "Garantía", value: product.warranty_info, icon: ShieldCheck },
  ].filter((f) => f.value);

  const prevPhoto = (e) => { e.stopPropagation(); setPhotoIndex((i) => (i === 0 ? photos.length - 1 : i - 1)); };
  const nextPhoto = (e) => { e.stopPropagation(); setPhotoIndex((i) => (i === photos.length - 1 ? 0 : i + 1)); };

  const handleInterest = () => {
    const amount = isAuction
      ? product.buy_now_price // fixed buy-now price for auctions
      : (lastPriceAmount ? parseFloat(lastPriceAmount) : listedPrice);
    submitOffer({ mode: "direct_purchase", amount });
  };

  const handleParticipate = () => {
    submitOffer({ mode: "offer", amount: parseFloat(bidAmount) });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="product-details-popup">
        <DialogHeader>
          <DialogTitle>Detalles del producto</DialogTitle>
          <DialogDescription className="sr-only">Información completa del producto y opciones de compra</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Photo carousel */}
          <div className="relative rounded-xl overflow-hidden bg-[#0F172A]" style={{ aspectRatio: "1/1" }}>
            {photos[photoIndex] && (
              <img
                src={photos[photoIndex]}
                alt={`${product.title} ${photoIndex + 1}`}
                onError={handleImgError}
                className="w-full h-full object-cover"
                data-testid="details-photo"
              />
            )}
            {photos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  data-testid="details-photo-prev"
                  aria-label="Foto anterior"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  data-testid="details-photo-next"
                  aria-label="Foto siguiente"
                >
                  <ChevronRight size={20} />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/55 text-white text-xs px-2.5 py-1 rounded-full">
                  {photoIndex + 1} / {photos.length}
                </div>
              </>
            )}
          </div>

          {/* Product name (bold) + description */}
          <div>
            <p className="text-xs text-[#64748B] uppercase tracking-wide font-semibold mb-1">Producto</p>
            <h2 className="text-lg font-bold text-[#0F172A]" data-testid="details-title">{product.title}</h2>
            <p className="text-sm text-[#374151] mt-2 leading-relaxed" data-testid="details-description">{product.description}</p>
          </div>

          {/* Ficha técnica */}
          {fichaTecnica.length > 0 && (
            <div className="rounded-xl border border-[#E2E8F0] overflow-hidden" data-testid="details-ficha-tecnica">
              <div className="bg-[#F8FAFC] px-3 py-2 border-b border-[#E2E8F0]">
                <p className="text-xs font-bold text-[#0F172A] uppercase tracking-wide">Ficha técnica</p>
              </div>
              <div className="divide-y divide-[#F1F5F9]">
                {fichaTecnica.map(({ label, value, icon: Icon }) => (
                  <div key={label} className="flex items-start gap-2.5 px-3 py-2.5">
                    <Icon size={15} className="text-[#64748B] mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-[#64748B]">{label}</p>
                      <p className="text-sm font-medium text-[#0F172A] break-words">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Already-requested banner */}
          {participation && (
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm text-sky-700" data-testid="details-participation-banner">
              {participation.type === "bid"
                ? `Ya tienes una oferta activa de $${participation.amount?.toLocaleString()} en esta subasta.`
                : "Ya enviaste una solicitud para este producto. El vendedor la revisará pronto."}
            </div>
          )}

          {/* COD policy */}
          <button
            type="button"
            onClick={() => setPolicyOpen(true)}
            className="w-full flex items-center justify-center gap-2 bg-[#FEF3C7] border border-amber-200 rounded-lg px-3 py-2"
            data-testid="details-cod-badge"
          >
            <ShieldCheck size={14} className="text-[#D97706] flex-shrink-0" />
            <span className="text-xs text-[#D97706] font-medium whitespace-nowrap truncate">
              Pago contraentrega: revisas y luego pagas →
            </span>
          </button>

          {/* Price / offer section */}
          {isAuction ? (
            <div className="space-y-2">
              <Label htmlFor="details-bid-amount">Tu oferta (BiiD)</Label>
              <p className="text-xs text-[#64748B]">
                Oferta actual: <span className="font-semibold">${currentBid.toLocaleString()}</span>
              </p>
              <Input
                id="details-bid-amount"
                type="number"
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                placeholder={`Mínimo $${currentBid + 1}`}
                className="text-lg font-semibold"
                data-testid="details-bid-amount"
              />
            </div>
          ) : (
            <div className="space-y-2">
              {product.accepts_offers === false ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center" data-testid="details-fixed-price">
                  <p className="text-xs text-emerald-600 mb-0.5">Precio publicado (precio fijo)</p>
                  <p className="text-xl font-bold text-emerald-700">${listedPrice?.toLocaleString()}</p>
                </div>
              ) : (
                <>
                  <Label htmlFor="details-last-price">
                    Último precio <span className="text-[#64748B] text-xs font-normal">(opcional)</span>
                  </Label>
                  <Input
                    id="details-last-price"
                    type="number"
                    value={lastPriceAmount}
                    onChange={(e) => setLastPriceAmount(e.target.value)}
                    placeholder={`$${listedPrice || 0}`}
                    className="text-lg font-semibold"
                    data-testid="details-last-price"
                  />
                  <p className="text-xs text-[#64748B]">
                    Si lo dejas vacío, se solicitará al precio publicado: ${listedPrice?.toLocaleString()}
                  </p>
                </>
              )}
            </div>
          )}

          {/* Identity / registration - embedded, never a second pop-up */}
          <IdentityFields
            testidPrefix="details"
            sessionWhatsapp={sessionWhatsapp}
            maskedUsername={maskedUsername}
            whatsapp={whatsapp}
            setWhatsapp={setWhatsapp}
            checkingWhatsapp={checkingWhatsapp}
            userExists={userExists}
            whatsappError={whatsappError}
            username={username}
            setUsername={setUsername}
            confirmWhatsappChecked={confirmWhatsappChecked}
            setConfirmWhatsappChecked={setConfirmWhatsappChecked}
          />

          {/* Final action buttons */}
          <div className="flex gap-2 pt-1">
            {isAuction && (
              <Button
                onClick={handleParticipate}
                disabled={loading || !bidAmount || whatsapp.length !== 8 || (userExists === false && (!username || !confirmWhatsappChecked))}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="details-participate-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Gavel size={16} className="mr-1.5" /> Participar</>)}
              </Button>
            )}
            {(!isAuction || product.buy_now_price) && (
              <Button
                onClick={handleInterest}
                disabled={loading || whatsapp.length !== 8 || (userExists === false && (!username || !confirmWhatsappChecked))}
                className="flex-1 bg-[#34D399] hover:bg-[#10B981] text-white"
                data-testid="details-interest-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><Tag size={16} className="mr-1.5" /> Me interesa{isAuction ? ` $${product.buy_now_price?.toLocaleString()}` : ""}</>
                )}
              </Button>
            )}
          </div>

          <p className="text-xs text-center text-[#94A3B8]">
            Al enviar, aceptas recibir notificaciones en tu WhatsApp
          </p>
        </div>

        <PaymentInfoModal open={policyOpen} onOpenChange={setPolicyOpen} />
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetailsPopup;
